import React, { useState, useCallback, useEffect } from "react";

import { Helmet } from 'react-helmet-async';

import {
    Box,
    Divider,
    TextField,
    Grid,
    Typography,
    Switch,
    FormControlLabel,
    styled,
    Card,
    Select,
    MenuItem,
    FormControl,
    FormHelperText,
    InputLabel,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Chip,
    ListItemButton,
    List,
    Collapse,
    Button
} from '@mui/material';

import { grey } from "@mui/material/colors";

import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from 'formik';
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonRestricted from '../../common/CommonRestricted';

import { initialValues, validationSchema } from "../../../schema/user_roles/userRolesSchema";
import { useUserRoles, roleTypes, targetTypes } from "../../../hooks/api/useUserRoles";
import { useUsers, userTypes, targetTypes as userTargetTypes } from "../../../hooks/api/useUsers";
import { useBranchUsers, branchUserTypes } from "../../../hooks/api/useBranchUsers";
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useAppContextFeatures } from '../../../context/AppContext';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from "../../../hooks/useTranslations";

import UpdatePermissions from "../permissions/UpdatePermissions"
import { capitalizeString } from "../../../utils/formatString";
import { checkValueExists } from "../../../utils/dataSchema";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

const getCurrentSelectValues = ({ data, matchDataField = 'user_id', matchArray }) => {

    let val = [];

    matchArray.forEach(e1 => {
        const checkExist = data.filter(i => i[matchDataField] === e1)

        if (checkExist.length) {
            val.push(e1)
        }

    })

    return val

}

const RenderUserOptionItemDefault = (props) => {

    const {
        t,
        item,
        // values,
        // dataUsers
    } = props

    let display = `${item?.firstname} ${item?.lastname} (${item?.username})`

    if (item?.is_owner) {
        display = `[${t("ownerAccount")}] - ${display}`
    }

    return (
        <>
            <ListItemText
                primary={
                    <Typography
                        sx={{
                            fontSize: 15,
                            fontWeight: '600'
                        }}
                    >
                        {display}
                    </Typography>
                }
            />
        </>
    )
}

const RenderUserOptionItemRetail = (props) => {

    const {
        item,
        values,
        viewType = 'main',
        dataUsers
    } = props

    let secondary = ''

    if (viewType === "main") {
        secondary = `${item?.company_name} (${item?.branch_user_count} Users)`
    } else { // for branch
        // check the parent name
        let parentUserId = item?.parent_user_id

        const check = dataUsers.filter(i => i.user_id === parentUserId)

        secondary = check.length ? check[0].company_name : "-"

    }

    return (
        <>
            <ListItemText
                primary={
                    <Typography
                        sx={{
                            fontSize: 15,
                            fontWeight: '600'
                        }}
                    >
                        {`${item?.firstname} ${item?.lastname}`} ({`${item?.username}`})
                    </Typography>
                }
                secondary={
                    <Typography
                        sx={{
                            fontSize: 13,
                            // fontWeight: '600'
                        }}
                    >
                        {secondary}
                    </Typography>
                }
            />
        </>
    )
}

const RenderUserOptionItemCustomer = (props) => {

    const {
        item,
        values,
        viewType = 'main',
        dataUsers
    } = props

    let secondary = ''

    if (viewType === "main") {
        secondary = `${item?.company_name} (${item?.branch_user_count} Users)`
    } else { // for branch
        // check the parent name
        let parentUserId = item?.parent_user_id

        const check = dataUsers.filter(i => i.user_id === parentUserId)

        secondary = check.length ? check[0].company_name : "-"

    }

    return (
        <>
            <ListItemText
                primary={
                    <Typography
                        sx={{
                            fontSize: 15,
                            fontWeight: '600'
                        }}
                    >
                        {`${item?.firstname} ${item?.lastname}`} ({`${item?.username}`})
                    </Typography>
                }
                secondary={
                    <Typography
                        sx={{
                            fontSize: 13,
                            // fontWeight: '600'
                        }}
                    >
                        {secondary}
                    </Typography>
                }
            />
        </>
    )
}

const RenderUserOptionItemWorkstation = (props) => {

    const {
        item,
        // values,
        // dataUsers
    } = props

    return (
        <>
            <ListItemText
                primary={
                    <Typography
                        sx={{
                            fontSize: 15,
                            fontWeight: '600'
                        }}
                    >
                        {`${item?.firstname} ${item?.lastname}`} ({`${item?.username}`})
                    </Typography>
                }
                secondary={
                    <Typography
                        sx={{
                            fontSize: 13,
                            // fontWeight: '600'
                        }}
                    >
                        {`${item?.location_count} Locations`}
                    </Typography>
                }
            />
        </>
    )
}

const RenderUserOptionItem = (props) => {

    const { values } = props

    // for retail
    if (values?.userRouteType === routeTypes.retail) {
        return (
            <RenderUserOptionItemRetail
                {...props}
            />
        )
    }

    // for customer
    if (values?.userRouteType === routeTypes.customer) {
        return (
            <RenderUserOptionItemCustomer
                {...props}
            />
        )
    }

    // for worker
    if (values?.userRouteType === routeTypes.workstation) {
        return (
            <RenderUserOptionItemWorkstation
                {...props}
            />
        )
    }

    // for normal user
    return (
        <RenderUserOptionItemDefault
            {...props}
        />
    )

}

const RenderBranchUsersSelect = ({ t, formik, dataUsers, dataBranchUsers }) => {

    const { values, errors, setFieldValue } = formik

    // console.log('dataUsers', dataUsers)

    return (
        <FormControl
            margin="dense"
            fullWidth
            size="small"
            error={Boolean(errors?.branchUserId)}
        >
            {/* {JSON.stringify(values?.branchUserId)} */}
            <InputLabel id="userId">{t('branchUsers')}</InputLabel>
            <Select
                fullWidth
                size="small"
                margin="dense"
                labelId="branchUserId"
                label={t("branchUsers")}
                name={"branchUserId"}
                onChange={(event) => {

                    let val = event.target.value;
                    setFieldValue('branchUserId', val)

                }}
                value={
                    getCurrentSelectValues({
                        data: dataBranchUsers,
                        matchArray: values?.branchUserId
                    })
                }
                renderValue={(selected) => {

                    return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {
                                selected.map((value, index2) => {

                                    // get back the username based on the id
                                    const user = dataBranchUsers.filter(i => i.user_id === value)

                                    const userName = user.length ? user[0].username : '-'

                                    return (
                                        <Chip key={index2} label={userName} />
                                    )
                                })
                            }
                        </Box>
                    )

                }}
                multiple
                input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
            >
                {
                    dataBranchUsers.map((i, index) => {
                        return (
                            <MenuItem
                                key={index}
                                value={i?.user_id}
                                divider
                            >
                                <RenderUserOptionItem
                                    t={t}
                                    item={i}
                                    values={values}
                                    viewType={`branch`}
                                    dataUsers={dataUsers}
                                />
                            </MenuItem>
                        )
                    })
                }
            </Select>
            <FormHelperText>
                {errors?.userId}
            </FormHelperText>

        </FormControl>
    )
}

const RenderMainUsersSelect = ({ t, formik, dataUsers }) => {

    const { values, errors, setFieldValue } = formik

    return (
        <FormControl
            margin="dense"
            fullWidth
            size="small"
            error={Boolean(errors?.userId)}
        >
            <InputLabel id="userId">{t('mainUsers')}</InputLabel>
            <Select
                fullWidth
                size="small"
                margin="dense"
                label={t("mainUsers")}
                name={"userId"}
                onChange={async (event) => {

                    let val = event.target.value;
                    setFieldValue('userId', val)

                    if (val.length <= 0) setFieldValue(`branchUserId`, [])

                }}
                value={
                    values?.userId
                    // getCurrentSelectValues({
                    //     data: dataUsers,
                    //     matchArray: values?.userId
                    // })
                }
                renderValue={(selected) => {

                    return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {
                                selected.map((value, index2) => {

                                    // get back the username based on the id
                                    const user = dataUsers.filter(i => i.user_id === value)

                                    const userName = user.length ? user[0].username : '-'

                                    return (
                                        <Chip key={index2} label={userName} />
                                    )
                                })
                            }
                        </Box>
                    )

                }}
                multiple
                input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
            >
                {
                    dataUsers.map((i, index) => {
                        return (
                            <MenuItem
                                key={index}
                                value={i?.user_id}
                                divider
                            >
                                <RenderUserOptionItem
                                    t={t}
                                    item={i}
                                />
                            </MenuItem>
                        )
                    })
                }
            </Select>
            <FormHelperText>
                {errors?.userId}
            </FormHelperText>

            {/* {JSON.stringify(values.userId)} */}
        </FormControl>
    )
}

const RenderRolesInfo = ({ t, formik, dataUsers, dataBranchUsers }) => {

    const { values, errors, setFieldValue, handleChange } = formik

    return (
        <>
            <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                <CommonSectionHeader
                    title={t('rolesAssignation')}
                    subTitle={t('addingRoleTitleWithDetails')}
                />
            </Grid>

            <Grid item xs={12} sm={12} md={9}>
                <Grid container spacing={1}>

                    <Grid item xs={12} md={6} textAlign="left">
                        <FormControlLabel
                            control={
                                <Switch
                                    name="status"
                                    checked={Boolean(values?.status)}
                                    onChange={(event, checked) => {
                                        setFieldValue("status", checked);
                                    }}
                                />
                            }
                            label={
                                Boolean(values.status) ? t("enabled") : t("disabled")
                            }
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <FormControl
                            margin="dense"
                            fullWidth
                            size="small"
                            error={Boolean(errors.userRouteType)}
                        >
                            <InputLabel id="userRouteType">{t('userRouteType')}</InputLabel>
                            <Select
                                disabled
                                labelId="userRouteType"
                                label={t('userRouteType')}
                                name={"userRouteType"}
                                onChange={(event) => {
                                    setFieldValue(`userRouteType`, event.target.value)
                                }}
                                value={values?.userRouteType ? values.userRouteType : ""}
                                sx={{
                                    textAlign: 'left'
                                }}
                            >
                                {
                                    Object.keys(routeTypes).map((i, index) => {
                                        return (
                                            <MenuItem key={index} value={i}>{i}</MenuItem>
                                        )
                                    })
                                }
                            </Select>
                            <FormHelperText>
                                {errors?.userRouteType}
                            </FormHelperText>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={12} md={12}>
                        <TextField
                            margin="dense"
                            size="small"
                            autoFocus
                            fullWidth
                            name={"roleTitle"}
                            label={t("roleTitle")}
                            type="text"
                            variant="outlined"
                            value={values?.roleTitle}
                            onChange={handleChange}
                            error={Boolean(errors?.roleTitle)}
                            helperText={errors?.roleTitle}
                        />
                    </Grid>

                    <Grid item xs={12} sm={12} md={6}>

                        <RenderMainUsersSelect
                            t={t}
                            formik={formik}
                            dataUsers={dataUsers}
                        />

                    </Grid>

                    {
                        (
                            values?.userRouteType === routeTypes.retail ||
                            values?.userRouteType === routeTypes.customer
                        ) &&
                        <>

                            <Grid item xs={12} sm={12} md={6}>

                                <RenderBranchUsersSelect
                                    t={t}
                                    formik={formik}
                                    dataUsers={dataUsers}
                                    dataBranchUsers={dataBranchUsers}
                                />

                            </Grid>
                        </>
                    }

                    <Grid item xs={12} sm={12} md={12}>
                        <TextField
                            margin="dense"
                            size="small"
                            autoFocus
                            fullWidth
                            multiline
                            rows={3}
                            name={"description"}
                            label={t("description")}
                            type="password"
                            variant="outlined"
                            value={values?.description}
                            onChange={handleChange}
                            error={Boolean(errors?.description)}
                            helperText={errors?.description}
                        />
                    </Grid>

                </Grid>
            </Grid>
        </>
    )
}

const RenderForm = ({ id, routeType, t, formik, dataUsers, dataBranchUsers }) => {

    const { values } = formik

    if (!values) return <></>

    return (
        <>
            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={1}>

                        <RenderRolesInfo
                            t={t}
                            formik={formik}
                            dataUsers={dataUsers}
                            dataBranchUsers={dataBranchUsers}
                        />

                    </Grid>
                </StyledCard>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <Button
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 1,
                                mb: 1
                            }}
                        >
                            {t('saveChanges')}
                        </Button>
                    </Grid>
                </Grid>

            </form>
            <UpdatePermissions
                roleId={id}
                routeType={routeType}
                permissionRouteType={values?.userRouteType}
            />
        </>

    )
}

export default function UpdateUserRole({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "layout", "@role"] })

    const { id } = useParams();

    const { promptMessage } = useSystemPrompt();
    const { isLoading, update, get, filterOperatorTypes } = useUserRoles({ roleType: roleTypes.role });
    const { list: listBranchUsers } = useBranchUsers({})
    const { list: listUsers } = useUsers({ userType: '', targetType: '' });
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.userRole.id, scopeTypes.userRole.role.id)

    const { setSystemLoading } = useAppContextFeatures();

    const navigate = useNavigate();
    const [savedValues, setSavedValues] = useState(null)
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const [dataUsers, setDataUsers] = useState([])
    const [dataBranchUsers, setDataBranchUsers] = useState([])
    // const [dataBranches, setDataBranches] = useState([])

    const loadData = useCallback(async (id) => {

        setSystemLoading(true);

        try {

            const { data } = await get({ id })
            const { role } = data;
            const { users } = role

            const roleUserId = users.map((i) => {
                return i.user_id
            })

            setSavedValues({
                ...initialValues,
                roleTitle: role.role_name,
                userRouteType: role.role_type,
                description: role?.description ? role.description : "",
                status: role.status === '1',
                userId: roleUserId
            })

            // 
            await fetchUsers({ routeType: role.role_type })

            // load the branch users as well
            if (
                role?.role_type === routeTypes.retail ||
                role?.role_type === routeTypes.customer
            ) {
                await fetchBranchUsers({ routeType: role.role_type, parentUserId: roleUserId })
            }

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

    }, [id])

    const onSubmit = async (currentValues) => {

        try {

            let data = {
                role: {
                    role_name: currentValues?.roleTitle,
                    description: currentValues?.description,
                    status: currentValues?.status ? 1 : 0,
                },
                users:
                    currentValues?.branchUserId ?
                        currentValues?.userId.concat(currentValues.branchUserId)
                        :
                        currentValues?.userId

            }

            const response = await update({ id, data, scopeType: "", targetType: targetTypes.role });

            promptMessage(true, `${response?.message}`)
            navigate(getRoute("settings/userRoles"))

        } catch (error) {
            promptMessage(false, `Action Failed (${error?.message})`)
        }

    }

    const fetchUsers = async ({ routeType }) => {

        try {

            const props = {
                limit: -1,
                skip: 0,
                filterValue: [
                    // {
                    //     name: "ax_istrash",
                    //     operator: filterOperatorTypes.eq,
                    //     value: false
                    // }
                ],
                sortInfo: {
                    id: "username",
                    dir: 0
                }
            }

            if (routeType === routeTypes.admin) {

                props.userType = userTargetTypes.admin

            }
            if (routeType === routeTypes.retail) {

                props.userType = userTargetTypes.retail
                props.dataTypes = ["branch_user_count"]

            }
            if (routeType === routeTypes.customer) {

                props.userType = userTargetTypes.customer
                props.dataTypes = ["branch_user_count"]

            }
            if (routeType === routeTypes.workstation) {

                props.userType = userTargetTypes.worker
                props.dataTypes = ["location_count"]

            }

            const result = await listUsers(props)

            // console.log('result', result)

            let _dataUsers = []

            if (routeType === routeTypes.admin) {

                _dataUsers = result?.data?.administrators
            }
            if (routeType === routeTypes.retail) {

                _dataUsers = result?.data?.retailers
            }
            if (routeType === routeTypes.customer) {

                _dataUsers = result?.data?.customers
            }
            if (routeType === routeTypes.workstation) {

                _dataUsers = result?.data?.workers

            }

            setDataUsers(_dataUsers)


        } catch (error) {
            promptMessage(false, `Action Failed (${error?.message})`)
        }

        return true

    }

    const fetchBranchUsers = async ({ routeType, parentUserId }) => {

        try {

            let filterValues = []

            if (parentUserId.length) {
                filterValues.push({
                    name: "parent_user_id",
                    operator: filterOperatorTypes.anyOf,
                    value: JSON.stringify(parentUserId)
                })
            }

            const props = {
                limit: -1,
                skip: 0,
                filterValue: filterValues,
                sortInfo: {
                    id: "parent_user_id",
                    dir: 0
                }
            }

            if (routeType === routeTypes.retail) {

                props.branchUserType = userTargetTypes.retail

            }

            if (routeType === routeTypes.customer) {

                props.branchUserType = userTargetTypes.customer

            }

            const result = await listBranchUsers(props)

            setDataBranchUsers(result?.data?.branch_users)

            return result?.data?.branch_users

        } catch (error) {
            promptMessage(false, `Action Failed (${error?.message})`)
        }

        return true

    }

    useEffect(() => {

        loadData(id);
        // fetchUsers()

    }, [])

    const formik = useFormik({
        initialValues: savedValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;

    useEffect(() => {

        // setDataBranchUsers([])
        if (values?.userId) {
            if (
                values?.userRouteType === routeTypes.retail ||
                values?.userRouteType === routeTypes.customer
            ) {
                fetchBranchUsers({ routeType: values.userRouteType, parentUserId: values?.userId })
            }
        }

    }, [values?.userId])

    useEffect(() => {

        updateStates()

    }, [dataUsers])

    const updateStates = async () => {

        if (values?.userId) {
            if (values.userId.length <= 0) {
                setFieldValue(`branchUserId`, [])
            } else {
                // filter out the only the main users
                let mainUsersId = []


                values.userId.forEach(e1 => {

                    let found = false

                    dataUsers.forEach(e2 => {
                        if (e2.user_id === e1) {
                            found = true
                        }
                    })

                    if (found)
                        mainUsersId.push(e1)
                })

                setFieldValue(`userId`, mainUsersId)

                // filter out the only the branch users
                if (
                    values?.userRouteType === routeTypes.retail ||
                    values?.userRouteType === routeTypes.customer
                ) {
                    let mainBranchUsersId = []

                    let _dataBranchUsers = await fetchBranchUsers({ routeType: values?.userRouteType, parentUserId: mainUsersId })

                    values.userId.forEach(e1 => {

                        let found = false

                        _dataBranchUsers.forEach(e2 => {
                            if (e2.user_id === e1) {
                                found = true
                            }
                        })

                        if (found)
                            mainBranchUsersId.push(e1)
                    })

                    // console.log('userId', values?.userId, _dataBranchUsers)

                    setFieldValue(`branchUserId`, mainBranchUsersId)
                }
            }
        }

    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.updateRole })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t('updateRole')}
                breadcrumbs={[
                    {
                        displayText: t('roles'),
                        props: {
                            href: getRoute(`settings/userRoles`)

                        }
                    },
                    {
                        displayText: t('updateRole'),
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) ?
                    <RenderForm
                        id={id}
                        routeType={routeType}
                        t={t}
                        formik={formik}
                        dataUsers={dataUsers}
                        dataBranchUsers={dataBranchUsers}
                    />
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}